import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { getUnacknowledgedAlerts, acknowledgeAlert, getAlertsByType, getAcknowledgedAlerts } from '../../../services/alertService';
import { supabase } from '../../../lib/supabase';

const AlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('unacknowledged'); // 'unacknowledged' | 'acknowledged'

  useEffect(() => {
    loadAlerts();
  }, [filter, view]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      let result;

      if (view === 'acknowledged') {
        result = await getAcknowledgedAlerts(50);
      } else if (filter === 'all') {
        result = await getUnacknowledgedAlerts();
      } else {
        result = await getAlertsByType(filter, 50);
      }

      if (result?.success) {
        setAlerts(result?.data);
      }
    } catch (error) {
      console.error('Load alerts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      let result = await acknowledgeAlert(alertId, user?.id);

      if (result?.success) {
        setAlerts(alerts?.filter(a => a?.id !== alertId));
      }
    } catch (error) {
      console.error('Acknowledge alert error:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 border-error text-error';
      case 'high':
        return 'bg-warning/10 border-warning text-warning';
      case 'medium':
        return 'bg-primary/10 border-primary text-primary';
      case 'low':
        return 'bg-muted border-border text-muted-foreground';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return 'AlertOctagon';
      case 'high':
        return 'AlertTriangle';
      case 'medium':
        return 'AlertCircle';
      case 'low':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      'negative_margin': 'Negative Margin',
      'ai_cost_spike': 'AI Cost Spike',
      'unusual_usage': 'Unusual Usage',
      'risk_detected': 'Risk Detected',
      'off_platform_contact': 'Off-Platform Contact',
      'suspicious_signup': 'Suspicious Signup',
      'account_suspension': 'Account Suspension',
      'abuse_detected': 'Abuse Detected'
    };
    return labels?.[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">System Alerts</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {alerts?.length} {view === 'unacknowledged' && filter === 'all' ? 'unacknowledged' : view === 'acknowledged' ? 'acknowledged' : ''} alerts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => { setView('unacknowledged'); setFilter('all'); }}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                view === 'unacknowledged'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              Unacknowledged
            </button>
            <button
              onClick={() => setView('acknowledged')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                view === 'acknowledged'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              Acknowledged
            </button>
          </div>
          {view === 'unacknowledged' && (
            <select
              value={filter}
              onChange={(e) => setFilter(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Types</option>
              <option value="negative_margin">Negative Margin</option>
              <option value="ai_cost_spike">AI Cost Spikes</option>
              <option value="unusual_usage">Unusual Usage</option>
              <option value="risk_detected">Risk Detected</option>
              <option value="off_platform_contact">Off-Platform Contact</option>
            </select>
          )}
        </div>
      </div>

      {alerts?.length > 0 ? (
        <div className="space-y-3">
          {alerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`border rounded-lg p-4 ${getSeverityColor(alert?.severity)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <Icon name={getSeverityIcon(alert?.severity)} size={20} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm">{alert?.title}</h4>
                      <span className="text-xs px-2 py-0.5 bg-background/50 rounded">
                        {getAlertTypeLabel(alert?.alert_type)}
                      </span>
                    </div>
                    <p className="text-sm opacity-90 mb-2">{alert?.message}</p>
                    <p className="text-xs opacity-70">
                      {new Date(alert?.created_at)?.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    {view === 'acknowledged' && alert?.acknowledged_at && (
                      <p className="text-xs opacity-60 mt-1">
                        Acknowledged {new Date(alert?.acknowledged_at)?.toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                  </div>
                </div>
                {!alert?.acknowledged && (
                  <button
                    onClick={() => handleAcknowledge(alert?.id)}
                    className="ml-3 px-3 py-1.5 bg-background hover:bg-background/80 border border-border rounded text-xs font-medium transition-smooth"
                  >
                    Acknowledge
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
          <p className="text-muted-foreground">No alerts to display</p>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;